const CONTACT_CHANNELS_BY_KIND: Record<ContactPointKind, ContactChannel[]> = {
  EMAIL: ["EMAIL"],
  MOBILE: ["SMS", "WHATSAPP", "PHONE"],
  FIXED_LINE: ["PHONE"],
  ADDRESS: ["LETTER"],
}

const normalizeText = (value: unknown) => `${value ?? ""}`.trim()

const normalizeChannel = (value: unknown): ContactChannel | null => {
  const normalized = normalizeText(value).toUpperCase()

  if (
    [
      "EMAIL",
      "SMS",
      "WHATSAPP",
      "MOBILE_MESSAGE",
      "PHONE",
      "LETTER",
      "WEB_BOT",
      "VOICE_BOT",
    ].includes(normalized)
  ) {
    return normalized as ContactChannel
  }

  return null
}

export const channelsForContactPointKind = (
  kind: ContactPointKind | string | null | undefined,
): ContactChannel[] => {
  const normalized = normalizeText(kind).toUpperCase() as ContactPointKind

  return [...(CONTACT_CHANNELS_BY_KIND[normalized] ?? [])]
}

export const defaultEnabledChannelsForKind = (
  kind: ContactPointKind | string | null | undefined,
): ContactChannel[] => channelsForContactPointKind(kind)

export const normalizeEnabledChannelsForKind = (
  kind: ContactPointKind | string | null | undefined,
  channels: readonly unknown[],
): ContactChannel[] => {
  const allowed = channelsForContactPointKind(kind)
  const normalized = channels
    .map(normalizeChannel)
    .filter((channel): channel is ContactChannel => Boolean(channel))
  const selected = new Set(normalized)

  return allowed.filter((channel) => selected.has(channel))
}

export const effectiveChannelPermissionsForContactPoint = (
  contact: ContactPoint,
): ContactPointChannelPermission[] => {
  const allowed = channelsForContactPointKind(contact.kind)
  const byChannel = new Map<ContactChannel, ContactPointChannelPermission>()
  const permissionForChannel = (
    permission: ContactPointChannelPermission,
    channel: ContactChannel,
  ): ContactPointChannelPermission => ({
    ...permission,
    channel,
  })

  for (const permission of contact.channel_permissions ?? []) {
    const channel = normalizeChannel(permission.channel)

    if (!channel) continue

    if (channel === "MOBILE_MESSAGE") {
      if (!byChannel.has("SMS")) byChannel.set("SMS", permissionForChannel(permission, "SMS"))

      if (!byChannel.has("WHATSAPP")) {
        byChannel.set("WHATSAPP", permissionForChannel(permission, "WHATSAPP"))
      }

      continue
    }

    byChannel.set(channel, permission)
  }

  return allowed.map((channel) => {
    const existing = byChannel.get(channel)

    return (
      existing ?? {
        id: null,
        organization_id: contact.organization_id,
        contact_point_id: contact.id,
        channel,
        is_enabled: true,
        source: null,
        disabled_reason: null,
      }
    )
  })
}

export const enabledChannelsForContactPoint = (contact: ContactPoint): ContactChannel[] =>
  effectiveChannelPermissionsForContactPoint(contact)
    .filter((permission) => permission.is_enabled)
    .map((permission) => permission.channel)

const sameChannelSet = (left: readonly ContactChannel[], right: readonly ContactChannel[]) => {
  if (left.length !== right.length) return false

  const rightSet = new Set(right)

  return left.every((channel) => rightSet.has(channel))
}

export const enabledChannelSelectionChanged = (
  contact: ContactPoint | null | undefined,
  kind: ContactPointKind,
  selectedChannels: readonly unknown[],
) => {
  const normalized = normalizeEnabledChannelsForKind(kind, selectedChannels)
  const baseline = contact
    ? enabledChannelsForContactPoint(contact)
    : defaultEnabledChannelsForKind(kind)

  return !sameChannelSet(normalized, normalizeEnabledChannelsForKind(kind, baseline))
}
